import type { CurrentUser } from "@/lib/auth";
import { api } from "@/lib/apiClient";
import {
  DEFAULT_HOME_PAGE,
  readUserHomePage,
  writeUserHomePage,
} from "@/lib/userHomePage";

const LEGACY_FAVORITES_KEY = "eh-nav-favorites";
const FAVORITES_PREFIX = "eh-nav-favorites:";

const HREF_RE =
  /^\/(?:[A-Za-z0-9._~-]+(?:\/[A-Za-z0-9._~-]+)*)?(?:\?[A-Za-z0-9._~%=&+-]*)?$/;

export interface UserUiPreferences {
  home_page: string;
  favorite_hrefs: string[];
  stored: boolean;
}

export function isSafeAppHref(value: string): boolean {
  return value.length <= 200 && HREF_RE.test(value) && !value.split("/").includes("..");
}

function preferenceUserId(user: CurrentUser | null | undefined): string | null {
  if (!user) return null;
  const id = String(user.oid || user.sub || user.username || "").trim();
  return id || null;
}

function cacheKey(user: CurrentUser): string {
  return preferenceUserId(user) || "session";
}

function favoritesStorageKey(user: CurrentUser | null | undefined): string | null {
  const id = preferenceUserId(user);
  return id ? `${FAVORITES_PREFIX}${id}` : null;
}

function readJsonArray(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

/** Benutzerbezogene Kopie, sonst die frühere geräteweite Liste. */
export function readLocalFavorites(user: CurrentUser | null | undefined): string[] {
  try {
    const scopedKey = favoritesStorageKey(user);
    if (scopedKey && localStorage.getItem(scopedKey) !== null) {
      return readJsonArray(localStorage.getItem(scopedKey));
    }
    return readJsonArray(localStorage.getItem(LEGACY_FAVORITES_KEY));
  } catch {
    return [];
  }
}

export function writeLocalFavorites(user: CurrentUser | null | undefined, hrefs: string[]): void {
  const key = favoritesStorageKey(user);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(hrefs));
  } catch {
    /* ignore quota */
  }
}

function writeMirrors(user: CurrentUser, value: UserUiPreferences): void {
  writeUserHomePage(user, value.home_page);
  writeLocalFavorites(user, value.favorite_hrefs);
}

let cached: UserUiPreferences | null = null;
let cachedFor: string | null = null;
let inflight: Promise<UserUiPreferences> | null = null;
let inflightFor: string | null = null;

export function rememberUserUiPreferences(user: CurrentUser, value: UserUiPreferences): void {
  cached = value;
  cachedFor = cacheKey(user);
  writeMirrors(user, value);
}

async function fetchAndMigrate(user: CurrentUser): Promise<UserUiPreferences> {
  const remote = await api.get<UserUiPreferences>("/me/ui-preferences");
  if (remote.stored) return remote;

  const home = readUserHomePage(user);
  const homePage = isSafeAppHref(home) ? home : DEFAULT_HOME_PAGE;
  const favoriteHrefs = readLocalFavorites(user).filter(isSafeAppHref);
  if (homePage === DEFAULT_HOME_PAGE && favoriteHrefs.length === 0) {
    return { home_page: DEFAULT_HOME_PAGE, favorite_hrefs: [], stored: false };
  }
  return api.patch<UserUiPreferences>("/me/ui-preferences", {
    home_page: homePage,
    favorite_hrefs: favoriteHrefs,
  });
}

export function loadUserUiPreferences(user: CurrentUser): Promise<UserUiPreferences> {
  const key = cacheKey(user);
  if (cached && cachedFor === key) return Promise.resolve(cached);
  if (inflight && inflightFor === key) return inflight;
  inflightFor = key;
  inflight = fetchAndMigrate(user)
    .then((value) => {
      rememberUserUiPreferences(user, value);
      return value;
    })
    .finally(() => {
      if (inflightFor === key) inflight = null;
    });
  return inflight;
}

export async function fetchStoredUserUiPreferences(): Promise<UserUiPreferences> {
  return api.get<UserUiPreferences>("/me/ui-preferences");
}

export function saveUserUiPreferences(
  user: CurrentUser,
  patch: { home_page: string; favorite_hrefs: string[] },
): Promise<UserUiPreferences> {
  return api
    .patch<UserUiPreferences>("/me/ui-preferences", patch)
    .then((value) => {
      rememberUserUiPreferences(user, value);
      return value;
    });
}

export function listsEqual(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((item, index) => item === right[index]);
}
